/**
 * Default universal error rules
 */
export const DEFAULT_ERROR_RULES = {
    captureOn5xx: true,
    captureOn4xx: false,
    captureNetworkErrors: true,
    captureTimeouts: true,
    slowRequestThreshold: 2000, // 2s for user-facing requests
    largeResponseThreshold: 2_000_000, // 2MB - early warning
    logPerformance: false,
};
let state = {
    enabled: false,
    sentry: null,
    errorRules: DEFAULT_ERROR_RULES,
    captureErrors: false,
    dsn: undefined,
    environment: undefined,
};
/**
 * Initialize monitoring for the application
 * This is a single entry point that sets up Sentry and configures universal error rules
 *
 * @param config Monitoring configuration
 *
 * @example
 * ```typescript
 * import { initMonitoring } from '@repo/monitoring';
 *
 * initMonitoring({
 *   dsn: 'your-sentry-dsn',
 *   environment: 'production',
 *   errorRules: {
 *     slowRequestThreshold: 2000,
 *     captureOn4xx: true,
 *   },
 * });
 * ```
 */
export async function initMonitoring(config) {
    const enabled = config.enabled !== false;
    if (!enabled) {
        console.log('[Monitoring] Disabled');
        state = {
            enabled: false,
            sentry: null,
            errorRules: DEFAULT_ERROR_RULES,
            captureErrors: false,
            dsn: undefined,
            environment: undefined,
        };
        return;
    }
    // Determine if we should capture errors to Sentry
    // Default: only in production, unless explicitly set
    const shouldCaptureErrors = config.captureErrors !== undefined
        ? config.captureErrors
        : config.environment === 'production';
    // UNCOMMENT FOR DEBUGGING
    // console.log('[Monitoring] Configuration check:', {
    //   'config.captureErrors': config.captureErrors,
    //   'config.environment': config.environment,
    //   shouldCaptureErrors: shouldCaptureErrors,
    //   'config.dsn': config.dsn ? `${config.dsn.substring(0, 30)}...` : 'MISSING',
    // });
    // Update state immediately with the configuration (before async Sentry init)
    state = {
        enabled: true,
        sentry: null, // Will be set after Sentry loads
        errorRules: {
            ...DEFAULT_ERROR_RULES,
            ...config.errorRules,
        },
        captureErrors: shouldCaptureErrors,
        dsn: config.dsn,
        environment: config.environment,
    };
    try {
        // Dynamically import Sentry to avoid bundling it in packages
        const Sentry = await import('@sentry/react');
        // Initialize Sentry (v8 API)
        Sentry.init({
            dsn: config.dsn,
            environment: config.environment,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
            ],
            tracesSampleRate: config.environment === 'production' ? 0.1 : 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            ...config.sentryOptions,
            // Disable sending events if captureErrors is false (set AFTER sentryOptions to ensure it's not overridden)
            beforeSend: shouldCaptureErrors
                ? config.sentryOptions?.beforeSend
                : () => null, // Return null to prevent sending
        });
        // Update state with Sentry instance
        state.sentry = Sentry;
    }
    catch (error) {
        console.error('[Monitoring] Failed to initialize:', error);
        state = {
            enabled: false,
            sentry: null,
            errorRules: DEFAULT_ERROR_RULES,
            captureErrors: false,
            dsn: undefined,
            environment: undefined,
        };
    }
}
/**
 * Get the current monitoring state
 */
export function getMonitoringState() {
    return state;
}
/**
 * Check if monitoring is enabled
 */
export function isMonitoringEnabled() {
    return state.enabled && state.sentry !== null;
}
/**
 * Get the Sentry instance
 */
export function getSentry() {
    return state.sentry;
}
/**
 * Get the universal error rules
 */
export function getErrorRules() {
    return state.errorRules;
}
//# sourceMappingURL=monitoring.js.map